import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { Flex, Text, ActionButton } from "@adobe/react-spectrum";
import Close from "@spectrum-icons/workflow/Close";
import Alert from "@spectrum-icons/workflow/Alert";
import Info from "@spectrum-icons/workflow/Info";
import { RootState } from "../../../store/storeConfig";
import Emitter from "../../../emitter";
import {
  TELEMETRY_INFO,
  TELEMETRY_ERROR,
  BANNER_DISMISSED,
  BANNER_ACTION_CLICKED,
} from "../../../constants/events";
import "./GlobalBanner.css";

const LOG_PREFIX = "[Banner]: ";

interface GlobalBannerProps {
  className?: string;
}

const GlobalBanner: React.FC<GlobalBannerProps> = ({ className }) => {
  const agent = useSelector((state: RootState) => state.agent);
  const [dismissed, setDismissed] = useState(false);

  const aicConfig = agent?.aicConfig;
  
  // Get banner configuration from aicConfig (simplified naming)
  const bannerEnabled = aicConfig?.banner_enabled === "true";
  const bannerMessage = aicConfig?.banner_message;
  const bannerType = aicConfig?.banner_type || "info";
  const isDismissible = aicConfig?.banner_dismissible !== "false"; // Default to dismissible
  const linkText = aicConfig?.banner_link_text;
  const linkUrl = aicConfig?.banner_link_url;

  /**
   * Emit telemetry event
   */
  const emitTelemetry = useCallback((type: string, info: object) => {
    Emitter.emit(TELEMETRY_ERROR, {
      type: "notif",
      chan: TELEMETRY_INFO,
      body: {
        type,
        info: LOG_PREFIX + JSON.stringify(info),
        timestamp: new Date().toISOString(),
        username: agent?.email || "unknown",
        context: {
          agent: true,
        },
      },
    });
  }, [agent?.email]);

  const handleDismiss = useCallback(() => {
    emitTelemetry(BANNER_DISMISSED, {
      bannerType: "global",
      bannerVariant: bannerType,
      message: bannerMessage,
    });
    setDismissed(true);
  }, [emitTelemetry, bannerMessage, bannerType]);

  const handleLinkClick = useCallback(() => {
    // Only emit telemetry - the native <a> tag handles opening the link
    emitTelemetry(BANNER_ACTION_CLICKED, {
      bannerType: "global",
      action: "link_clicked",
      linkText,
      linkUrl,
    });
  }, [emitTelemetry, linkText, linkUrl]);

  // Don't render if banner is disabled, no message, or dismissed
  if (!bannerEnabled || !bannerMessage || dismissed) {
    return null;
  }

  const getIcon = () => {
    switch (bannerType) {
      case "error":
        return <Alert size="S" />;
      case "warning":
        return <Alert size="S" />;
      case "info":
      default:
        return <Info size="S" />;
    }
  };

  return (
    <div className={`global-banner global-banner--${bannerType} ${className || ""}`}>
      <Flex direction="row" alignItems="center" gap="size-150" width="100%">
        <div className="global-banner__icon">
          {getIcon()}
        </div>
        
        <Flex direction="row" flex={1} gap="size-100" alignItems="center" wrap="wrap">
          <Text UNSAFE_className="global-banner__message">
            {bannerMessage}
          </Text>
          
          {linkText && linkUrl && (
            <a
              className="global-banner__link"
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLinkClick}
            >
              {linkText}
            </a>
          )}
        </Flex>

        {isDismissible && (
          <ActionButton
            isQuiet
            onPress={handleDismiss}
            UNSAFE_className="global-banner__close"
            aria-label="Dismiss banner"
            UNSAFE_style={{ color: "#FFFFFF" }}
          >
            <Close size="S" />
          </ActionButton>
        )}
      </Flex>
    </div>
  );
};

export default GlobalBanner;

